"use client";
import { useState } from "react";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Label } from "./ui/Label";
import { toast } from "sonner";
import { authService } from "../services/auth";

interface LoginPageProps {
  onLogin: () => void;
}

export function LoginPage({ onLogin }: LoginPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar se ambos os campos estão preenchidos
    if (!email.trim() || !password.trim()) {
      toast.error("Por favor, preencha todos os campos");
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.login(email, password);

      // Store token
      const token = response.data.data?.token || response.data.token || response.data.access_token;
      if (token) {
        localStorage.setItem('sanctum_token', token);

        authService.oldAPIInitSession(token)
          .then(oldAccessToken => {
            if (oldAccessToken) {
              localStorage.setItem('accessToken', oldAccessToken.data.accessToken);
            }
          })
          .catch(e => {
            console.error("Old API init session failed", e);
          });
      }

      toast.success("Login realizado com sucesso!");
      onLogin();
    } catch (error: any) {
      console.error("Login error:", error);
      const message = error.response?.data?.message || "Erro ao realizar login. Verifique suas credenciais.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-[#F5F3EE] via-white to-[#F5F3EE] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            {/* Logo e Título */}
            <div className="flex flex-col items-center mb-8">
              <img
                src='/assets/logo.png'
                alt="Fora do Closet Logo"
                className="w-16 h-16 object-contain mb-4"
              />
              <h1 className="text-2xl font-semibold text-gray-900 text-center">
                FORA DO CLOSET
              </h1>
              <p className="text-sm text-gray-600 mt-2 text-center">
                Authentic Second Hand Luxury
              </p>
              <div className="w-16 h-1 bg-[#D4C070] rounded-full mt-4"></div>
            </div>

            {/* Subtítulo */}
            <div className="text-center mb-6">
              <h2 className="text-lg text-gray-800">
                Painel Administrativo
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Entre com suas credenciais
              </p>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">
                  E-mail
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 bg-[#F9F9F9] border-gray-200 focus:border-[#D4C070] focus:ring-[#D4C070]"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700">
                  Senha
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 bg-[#F9F9F9] border-gray-200 focus:border-[#D4C070] focus:ring-[#D4C070]"
                  disabled={isLoading}
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-[#D4C070] hover:bg-[#C4B060] text-white mt-6"
                disabled={isLoading}
              >
                {isLoading ? "Entrando..." : "Entrar"}
              </Button>
            </form>

            {/* Footer */}
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                Sistema de gestão administrativo
              </p>
            </div>
          </div>

          {/* Informação adicional */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              © 2025 Fora do Closet. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default LoginPage;
